import { Group, Stack, Text, ThemeIcon } from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconCpu,
  IconHourglassHigh,
  IconPlayerStopFilled,
  IconQuestionMark,
  IconReload,
  IconX
} from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { DeploymentStatus } from '@/types/deployment'
import { getRelativeTimeFromNow } from '@/utils/date'

// "Active" statuses imply the device should be doing something. When the device
// is offline we can't confirm these, so we show "Unknown" instead. Terminal /
// edge-owned states (Inactive, Failed, Stopping, Stopped, Removing) stay as-is —
// they remain meaningful regardless of connectivity.
const OFFLINE_UNKNOWN_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.InProgress,
  DeploymentStatus.Running,
  DeploymentStatus.Recovering,
  DeploymentStatus.Initializing,
  DeploymentStatus.LoadingModel,
  DeploymentStatus.PreparingOcr,
  DeploymentStatus.RegisteringMilestone,
  DeploymentStatus.ConnectingVideoStream,
  DeploymentStatus.DownloadingConfig,
  DeploymentStatus.DownloadingModel,
  DeploymentStatus.CheckingDockerManifest,
  DeploymentStatus.DownloadingImage,
  DeploymentStatus.QueuedForEngineBuild,
  DeploymentStatus.BuildingEngine,
  DeploymentStatus.Degraded
])

type StatusIconProps = {
  status: DeploymentStatus
}

const StatusIcon = ({ status }: StatusIconProps) => {
  if (status === DeploymentStatus.Running) {
    return (
      <ThemeIcon color="green" radius="xl" size="xs">
        <IconCheck style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.Failed) {
    return (
      <ThemeIcon color="red" radius="xl" size="xs">
        <IconX style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.Recovering) {
    return (
      <ThemeIcon color="orange" radius="xl" size="xs">
        <IconReload style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.Degraded) {
    return (
      <ThemeIcon color="orange" radius="xl" size="xs">
        <IconAlertTriangle style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.Inactive) {
    return (
      <ThemeIcon color="grey" radius="xl" size="xs">
        <IconX style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.Stopped) {
    return (
      <ThemeIcon color="grey" radius="xl" size="xs">
        <IconPlayerStopFilled
          style={{ width: '60%', height: '60%' }}
          stroke={2}
        />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.QueuedForEngineBuild) {
    return (
      <ThemeIcon variant="light" radius="xl" size="xs">
        <IconHourglassHigh style={{ width: '90%', height: '90%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (status === DeploymentStatus.BuildingEngine) {
    return (
      <ThemeIcon variant="light" radius="xl" size="xs">
        <IconCpu style={{ width: '90%', height: '90%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (
    status === DeploymentStatus.InProgress ||
    status === DeploymentStatus.Removing ||
    status === DeploymentStatus.Stopping ||
    status === DeploymentStatus.Initializing ||
    status === DeploymentStatus.LoadingModel ||
    status === DeploymentStatus.PreparingOcr ||
    status === DeploymentStatus.RegisteringMilestone ||
    status === DeploymentStatus.ConnectingVideoStream ||
    status === DeploymentStatus.DownloadingConfig ||
    status === DeploymentStatus.DownloadingModel ||
    status === DeploymentStatus.CheckingDockerManifest ||
    status === DeploymentStatus.DownloadingImage
  ) {
    return (
      <ThemeIcon variant="light" radius="xl" size="xs">
        <IconClock style={{ width: '90%', height: '90%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  return null
}

const statusTextMap: Partial<Record<DeploymentStatus, string>> = {
  [DeploymentStatus.Running]: 'deployments.status.running',
  [DeploymentStatus.Recovering]: 'deployments.status.recovering',
  [DeploymentStatus.Inactive]: 'deployments.status.inactive',
  [DeploymentStatus.Failed]: 'deployments.status.failed',
  [DeploymentStatus.InProgress]: 'deployments.status.inProgress',
  [DeploymentStatus.Removing]: 'deployments.status.removing',
  [DeploymentStatus.Stopping]: 'deployments.status.stopping',
  [DeploymentStatus.Stopped]: 'deployments.status.stopped',
  [DeploymentStatus.Initializing]: 'deployments.status.initializing',
  [DeploymentStatus.LoadingModel]: 'deployments.status.loadingModel',
  [DeploymentStatus.PreparingOcr]: 'deployments.status.preparingOcr',
  [DeploymentStatus.RegisteringMilestone]:
    'deployments.status.registeringMilestone',
  [DeploymentStatus.ConnectingVideoStream]:
    'deployments.status.connectingVideoStream',
  [DeploymentStatus.DownloadingConfig]: 'deployments.status.downloadingConfig',
  [DeploymentStatus.DownloadingModel]: 'deployments.status.downloadingModel',
  [DeploymentStatus.CheckingDockerManifest]:
    'deployments.status.checkingDockerManifest',
  [DeploymentStatus.DownloadingImage]: 'deployments.status.downloadingImage',
  [DeploymentStatus.QueuedForEngineBuild]:
    'deployments.status.queuedForEngineBuild',
  [DeploymentStatus.BuildingEngine]: 'deployments.status.buildingEngine',
  [DeploymentStatus.Degraded]: 'deployments.status.degraded'
}

type StatusTextProps = {
  status: DeploymentStatus
}

const StatusText = ({ status }: StatusTextProps) => {
  const messageId = statusTextMap[status]

  // Fall back to a generic label for any status we don't have a mapping for,
  // so an unknown/newer status code never crashes the indicator.
  if (!messageId) {
    return <FormattedMessage id="deployments.status.unknown" />
  }

  return <FormattedMessage id={messageId} />
}

// Short human-readable detail shown under the status label. Failed and
// Recovering are omitted on purpose — their detail is the deployment's
// error_body (for Recovering: the live startup phase + preserved last
// failure), rendered separately.
const statusDetailMap: Partial<Record<DeploymentStatus, string>> = {
  [DeploymentStatus.InProgress]: 'deployments.status.detail.inProgress',
  [DeploymentStatus.Running]: 'deployments.status.detail.running',
  [DeploymentStatus.Initializing]: 'deployments.status.detail.initializing',
  [DeploymentStatus.LoadingModel]: 'deployments.status.detail.loadingModel',
  [DeploymentStatus.PreparingOcr]: 'deployments.status.detail.preparingOcr',
  [DeploymentStatus.RegisteringMilestone]:
    'deployments.status.detail.registeringMilestone',
  [DeploymentStatus.ConnectingVideoStream]:
    'deployments.status.detail.connectingVideoStream',
  [DeploymentStatus.DownloadingConfig]:
    'deployments.status.detail.downloadingConfig',
  [DeploymentStatus.DownloadingModel]:
    'deployments.status.detail.downloadingModel',
  [DeploymentStatus.CheckingDockerManifest]:
    'deployments.status.detail.checkingDockerManifest',
  [DeploymentStatus.DownloadingImage]:
    'deployments.status.detail.downloadingImage',
  [DeploymentStatus.QueuedForEngineBuild]:
    'deployments.status.detail.queuedForEngineBuild',
  [DeploymentStatus.BuildingEngine]: 'deployments.status.detail.buildingEngine',
  [DeploymentStatus.Degraded]: 'deployments.status.detail.degraded'
}

type DeploymentStatusIndicatorProps = {
  status: DeploymentStatus
  statusUpdatedAt?: string | null
  deviceActive?: boolean | null
  detail?: ReactNode
}

export const DeploymentStatusIndicator = ({
  status,
  statusUpdatedAt,
  deviceActive,
  detail
}: DeploymentStatusIndicatorProps) => {
  // Device offline + an active status => we can't confirm the state. Show
  // "Unknown" (the last-known status returns automatically once it's back
  // online and the edge re-syncs).
  if (deviceActive === false && OFFLINE_UNKNOWN_STATUSES.has(status)) {
    return (
      <Stack gap={2}>
        <Group gap="xs">
          <ThemeIcon color="gray" radius="xl" size="xs">
            <IconQuestionMark
              style={{ width: '90%', height: '90%' }}
              stroke={2}
            />
          </ThemeIcon>

          <Text size="xs" fw="bold">
            <FormattedMessage id="deployments.status.unknown" />
          </Text>
        </Group>

        <Text size="xs" c="dimmed">
          <FormattedMessage id="deployments.status.unknown.detail" />
          {statusUpdatedAt && ` · ${getRelativeTimeFromNow(statusUpdatedAt)}`}
        </Text>
      </Stack>
    )
  }

  const detailId = statusDetailMap[status]
  const detailContent =
    detail ?? (detailId && <FormattedMessage id={detailId} />)

  return (
    <Stack gap={2}>
      <Group gap="xs">
        <StatusIcon status={status} />

        <Text size="xs" fw="bold">
          <StatusText status={status} />
        </Text>
      </Group>

      {(detailContent || statusUpdatedAt) && (
        <Text size="xs" c="dimmed">
          {detailContent}
          {detailContent && statusUpdatedAt && ' · '}
          {statusUpdatedAt && getRelativeTimeFromNow(statusUpdatedAt)}
        </Text>
      )}
    </Stack>
  )
}
